import React, { useState } from 'react';
import { Box, TableCell } from '@mui/material';
import StyledModal from '../../../../Components/GenericModal/StyledModal';
import GenericTable from '../../../../Components/GenericTable/GenericTable';
import { callPaginatedAPI } from '../../../../Components/Utilities/callAPI';
import queryString from 'query-string';
import {
  GenericTableState,
  GenericTableAction
} from '../../../../Components/GenericTable/genericTableReducer';
import { sharedClasses } from '../../../../Components/CustomUIElements/sharedClasses';
import FormattedDate from '../../../../Components/GenericTable/TableCells/FormattedDate';
import TokenUsageSearchBar from './TokenUsageSearchBar';

interface TokenUsage {
  id: number;
  kind: string;
  amount: number;
  candidate_name: string;
  created_at: string;
}

interface TokenUsageAPIReturn {
  data: {
    token_usages: TokenUsage[];
  };
  totalItems: number;
  totalPages: number;
}

const tokenUsageColumns = [
  { id: 'id', label: 'ID', sortable: true, canToggle: false, enabled: true },
  { id: 'kind', label: 'Kind', sortable: true, canToggle: false, enabled: true },
  { id: 'amount', label: 'Amount', sortable: true, canToggle: false, enabled: true },
  {
    id: 'candidate_name',
    label: 'Candidate Name',
    sortable: false,
    canToggle: false,
    enabled: true
  },
  { id: 'created_at', label: 'Created At', sortable: true, canToggle: false, enabled: true }
];

const TokenUsageTableRows = ({
  column,
  item
}: {
  column: GenericTableState['tableState']['columns'][number];
  item: TokenUsage;
}) => {
  if (!column.enabled) return null;

  switch (column.id) {
    case 'id':
      return <TableCell key={`${item.id}-${column.id}`}>{item.id}</TableCell>;
    case 'kind':
      return (
        <TableCell key={`${item.id}-${column.id}`} style={{ textTransform: 'capitalize' }}>
          {item.kind.replace('_', ' ')}
        </TableCell>
      );
    case 'amount':
      return <TableCell key={`${item.id}-${column.id}`}>{item.amount}</TableCell>;
    case 'candidate_name':
      return <TableCell key={`${item.id}-${column.id}`}>{item.candidate_name || '-'}</TableCell>;
    case 'created_at':
      return (
        <TableCell key={`${item.id}-${column.id}`}>
          {item.created_at ? <FormattedDate date={item.created_at} /> : '-'}
        </TableCell>
      );
    default:
      return <TableCell key={`${item.id}-${column.id}`}></TableCell>;
  }
};

const headerStyles = (name: (typeof tokenUsageColumns)[number]['id'] | 'checkbox' | 'actions') => {
  switch (name) {
    case 'id':
      return { minWidth: '80px' };
    case 'kind':
      return { minWidth: '120px' };
    case 'amount':
      return { minWidth: '100px' };
    case 'candidate_name':
      return { minWidth: '180px' };
    case 'created_at':
      return { minWidth: '160px' };
    default:
      return { minWidth: '90px' };
  }
};

const TokenUsageModalContent = React.memo(
  ({
    isOpen,
    handleClose,
    tableState,
    dispatch
  }: {
    isOpen: boolean;
    handleClose: () => void;
    tableState: GenericTableState;
    dispatch: React.Dispatch<GenericTableAction>;
  }) => {
    const [dateFilter, setDateFilter] = useState('all_time');
    const [tokenUsages, setTokenUsages] = useState<TokenUsageAPIReturn | null>(null);
    const [isFetching, setIsFetching] = useState(false);

    const fetchTokenUsages = React.useCallback(
      async (search: string, page = 1) => {
        setIsFetching(true);
        try {
          const params = {
            page: page || tableState.searchSettings.page,
            limit: tableState.searchSettings.limit,
            sort: tableState.searchSettings.sort,
            sort_order: tableState.searchSettings.sort_order,
            search,
            date_filter: dateFilter
          };

          const response = (await callPaginatedAPI(
            `/api/v4/token_usages?${queryString.stringify(params, {
              skipEmptyString: true,
              skipNull: true,
              arrayFormat: 'bracket',
              encode: false
            })}`,
            null,
            'GET'
          )) as TokenUsageAPIReturn;

          setTokenUsages(response);
        } catch (error) {
          console.error('Error fetching token usages:', error);
        } finally {
          setIsFetching(false);
        }
      },
      [
        tableState.searchSettings.page,
        tableState.searchSettings.limit,
        tableState.searchSettings.sort,
        tableState.searchSettings.sort_order,
        dateFilter
      ]
    );

    React.useEffect(() => {
      if (!isOpen) return;

      if (!tableState.searchQuery && tableState.searchSettings.page === 1 && !dateFilter) {
        fetchTokenUsages('', 1);
        return;
      }

      fetchTokenUsages(tableState.searchQuery, tableState.searchSettings.page);
    }, [
      isOpen,
      tableState.searchQuery,
      tableState.searchSettings.page,
      tableState.searchSettings.limit,
      tableState.searchSettings.sort,
      tableState.searchSettings.sort_order,
      dateFilter,
      fetchTokenUsages
    ]);

    return (
      <StyledModal
        isOpen={isOpen}
        handleClose={handleClose}
        label="Token Usages"
        modalTitle="Token Usages"
        styleOverrides={{
          maxWidth: '1200px',
          maxHeight: '70vh'
        }}
        subHeaderStyling={false}
      >
        <Box
          sx={{
            ...sharedClasses.mainModalContent,
            height: '100%',
            marginTop: '20px',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <TokenUsageSearchBar
            onSearchChange={(value) => {
              dispatch({ type: 'SET_SEARCH_QUERY', payload: value });
              dispatch({ type: 'SET_PAGE', payload: 1 });
            }}
            onDateFilterChange={(value) => {
              setDateFilter(value);
              dispatch({ type: 'SET_PAGE', payload: 1 });
            }}
            dateFilter={dateFilter}
          />
          <Box sx={{ flex: 1 }}>
            <GenericTable
              items={tokenUsages?.data?.token_usages || []}
              isLoadingItems={isFetching}
              dispatch={dispatch}
              tableState={tableState.tableState}
              paginationState={tableState.searchSettings}
              pagination={{
                totalItems: tokenUsages?.totalItems || 0,
                totalPages: tokenUsages?.totalPages || 0
              }}
              headerStyle={headerStyles}
              TableRows={TokenUsageTableRows}
              label="Token Usages"
              titleCellId="id"
              allowMultipleSelection={false}
              customEmptyStateMessage="No token usage records found"
              userPreferenceSource="token_usages"
            />
          </Box>
        </Box>
      </StyledModal>
    );
  }
);

TokenUsageModalContent.displayName = 'TokenUsageModalContent';

const TokenUsageModal = (props: {
  isOpen: boolean;
  handleClose: () => void;
  tableState: GenericTableState;
  dispatch: React.Dispatch<GenericTableAction>;
}) => {
  return <TokenUsageModalContent {...props} />;
};

export default TokenUsageModal;
